import React from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import breadcrumbMap, { dynamicBreadcrumbMap } from "../../utils/breadcrumbMap";

const safeParseUser = () => {
  try {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

const formatSegmentLabel = (segment) => {
  const cleaned = decodeURIComponent(segment).replace(/[-_]+/g, " ").trim();
  if (!cleaned) return "Page";
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

// For routes shaped like /basePath/:id, where no bare /basePath listing route exists,
// use the configured parent chain and append the id as a non-clickable final segment
// instead of letting buildFallbackSegments invent a link to a nonexistent /basePath route.
const buildDynamicSegments = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return null;

  const basePath = `/${parts[0]}`;
  const parentSegments = dynamicBreadcrumbMap[basePath];
  if (!parentSegments) return null;

  const idSegment = parts[parts.length - 1];
  return [...parentSegments, { label: formatSegmentLabel(idSegment) }];
};

const buildFallbackSegments = (pathname) => {
  if (!pathname || pathname === "/") return [];
  const parts = pathname.split("/").filter(Boolean);

  return parts.map((part, index) => {
    const to = `/${parts.slice(0, index + 1).join("/")}`;
    return {
      label: formatSegmentLabel(part),
      to: index === parts.length - 1 ? undefined : to,
    };
  });
};

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  const { tprmUser, tprmUseruser, caraUser } = useAuth();
  const { t } = useTranslation(["common", "sidebar", "breadcrumb"]);

  // Hide on dashboard home pages (exact or nested).
  if (
    pathname === "/assessment_dashboard" ||
    pathname.startsWith("/assessment_dashboard/") ||
    pathname === "/tprm_admin_dashboard" ||
    pathname.startsWith("/tprm_admin_dashboard/") ||
    pathname === "/vrm_portfolio_dashboard" ||
    pathname.startsWith("/vrm_portfolio_dashboard/")
  ) {
    return null;
  }

  // Determine home link based on user type/role
  const getHomeLink = () => {
    // Check for TPRM Admin (user_type = 14 or role = TPRM)
    if (tprmUser?.user_type === "14" || tprmUser?.user_type === 14) {
      return "/tprm_admin_dashboard";
    }
    
    // Check for TPRM User (user_type = 19 or role = TPRMUser)
    if (tprmUseruser?.user_type === "19" || tprmUseruser?.user_type === 19) {
      return "/tprm_admin_dashboard";
    }
    
    // Check role from localStorage as fallback
    const role = localStorage.getItem("role");
    if (role === "TPRM" || role === "TPRMUser" || role === "TPRM User") {
      return "/tprm_admin_dashboard";
    }
    if (role === "VRM") {
      return "/vrm_portfolio_dashboard";
    }

    // Check role_name from caraUser or stored user object
    const userData = safeParseUser();
    const role_name = (caraUser?.role_name || userData?.role_name || "").toLowerCase().trim();

    if (role_name === "internal auditor") {
      return "/internal_auditor_dashboard";
    }
    if (role_name === "external auditor") {
      return "/external_auditor_dashboard";
    }

    // Default to assessment dashboard
    return "/assessment_dashboard";
  };

  const homeLink = getHomeLink();
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const segments =
    breadcrumbMap[normalizedPath] ||
    buildDynamicSegments(normalizedPath) ||
    buildFallbackSegments(normalizedPath);

  return (
    <ol className="breadcrumb mt-2 ml-2 mb-0">
      <li className="breadcrumb-item">
        <Link to={homeLink} style={{ textDecoration: "none" }}>
          {t('label.home', { ns: 'breadcrumb', defaultValue: 'CARA' })}
        </Link>
      </li>
      {segments.map((seg, idx) => {
        const isLast = idx === segments.length - 1;
        const rawLabel = typeof seg === "string" ? seg : seg.label;
        const label =
          typeof seg === "object" && seg?.key
            ? t(seg.key, { ns: seg.ns, defaultValue: rawLabel })
            : rawLabel;
        const to = typeof seg === "string" ? undefined : seg.to;

        return (
          <li
            key={idx}
            className={`breadcrumb-item${isLast ? " active" : ""}`}
            aria-current={isLast ? "page" : undefined}
          >
            {isLast || !to ? (
              <span>{label}</span>
            ) : (
              <Link to={to} style={{ textDecoration: "none" }}>
                {label}
              </Link>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default Breadcrumbs;
